import { FC, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { PopoverProps, Typography } from '@mui/material';
import { DeleteOutline, MoreHoriz, PaletteOutlined } from '@mui/icons-material';

import { useModalContext } from '../modal/ModalContext';
import { useApiMutation } from '../query/hooks';
import { CustomPopover } from '../ui/CustomPopover';
import { ColorPicker } from '../components/ColorPicker';
import { graphql, getFragmentData, FragmentType } from '../gql';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { useSnackbar } from '../store/snackbar';

const personaPopover_updatePersonaColor = graphql(`
	mutation personaPopover_updatePersonaColor($id: UUID!, $color: String!) {
		updatePersona(by: { id: $id }, data: { color: $color }) {
			ok
			errorMessage
		}
	}
`);

const personaPopover_personaFragment = graphql(`
	fragment personaPopover_personaFragment on Persona {
		id
		name
		color
	}
`);

type Props = {
	personaData: FragmentType<typeof personaPopover_personaFragment>;
};

const PersonaPopover: FC<Props> = ({ personaData }) => {
	const theme = useTheme();
	const intl = useIntl();
	const { setModal } = useModalContext();
	const persona = getFragmentData(personaPopover_personaFragment, personaData);
	const [colorPopoverOpen, setColorPopoverOpen] = useState<boolean>(false);
	const [colorPopoverAnchorEl, setColorPopoverAnchorEl] = useState<PopoverProps['anchorEl']>(null);
	const { mutate: updatePersonaColor } = useApiMutation(personaPopover_updatePersonaColor);
	const ref = useRef<HTMLLIElement>(null);
	const forceCloseCustomPopoverEvent = useRef<Event>(new Event('forceClosePopover-color-change'));
	const { openSnackbar } = useSnackbar();

	return (
		<>
			<CustomPopover
				layoutIcon={<MoreHoriz />}
				forceCloseEvent={forceCloseCustomPopoverEvent.current}
				closeOnAction={false}
				options={[
					{
						icon: <PaletteOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
						caption: <FormattedMessage id={'persona.popover.set-color'} />,
						ref: ref,
						onAction: () => {
							const getBoundingClientRect = () => {
								return ref.current!.getBoundingClientRect();
							};

							setColorPopoverOpen((prevState) => !prevState);
							setColorPopoverAnchorEl({ getBoundingClientRect, nodeType: 1 });
						},
					},
					// Hidden as for now we want to show the export directly on the page, later on it will return here
					// {
					// 	icon: <DownloadOutlined sx={{ fontSize: 16, fill: theme.palette.grey[500] }} />,
					// 	caption: <FormattedMessage id={'common.export-pdf'} />,
					// 	onAction: () => generatePdf(),
					// },
					{
						icon: <DeleteOutline sx={{ fontSize: 16, fill: theme.palette.error.main }} />,
						caption: (
							<Typography color={theme.palette.error.main}>
								<FormattedMessage id={'persona.popover.delete'} />
							</Typography>
						),
						onAction: () => setModal({ type: 'deletePersona', payload: { id: persona.id, name: persona.name } }),
					},
				]}
			/>
			<ColorPicker
				open={colorPopoverOpen}
				defaultValue={persona.color}
				anchorEl={colorPopoverAnchorEl}
				onClose={() => setColorPopoverOpen(false)}
				onChange={(event) => {
					updatePersonaColor(
						{ id: persona.id, color: event.target.value },
						{
							onSuccess: (result) =>
								mutationResponseHandler({
									result: result.updatePersona.ok,
									toastMessages: {
										error: intl.formatMessage({ id: 'common.error.default' }),
									},
									errorMessage: result.updatePersona.errorMessage,
									callback: () => {
										setColorPopoverOpen(false);
										window.dispatchEvent(forceCloseCustomPopoverEvent.current);
									},
									openSnackbar,
								}),
						},
					);
				}}
			/>
		</>
	);
};

export { PersonaPopover };
